import { useContext, useState } from "react";
import { useMutation, useQueries } from "react-query";
import { useSelector } from "react-redux";
import { cleanObject, getRandomIntInclusive } from "../../../helpers";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import {
  approveOrRejectExpenseWithoutDisbursement,
  disburseExpenseFunds,
  getExpenseCategories,
} from "../../../services/expenses";
import { useGetAllChartOfAccounts } from "../../../hooks/api/queries";
import {
  fetchBankList,
  resolveAccountNumber,
} from "../../../services/transfers";
import { useMakeRefundStatusMutation } from "../../../hooks/api/mutations/useSettings";
import { useGetExpenseDepartments } from "../../../hooks/api/queries/useExpenses";
import { useCancelExpenseRecurrenceMutation } from "../../../hooks/api/mutations/expenses";

const useExpenseReview = ({ closeWidget, expense, handleFetchExpenses }) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const [responseStatus, setResponseStatus] = useState(null);
  const [isDisbursmentForm, setIsDisbursmentForm] = useState(false);
  const [isConfirmingCancelRecurrence, setIsConfirmingCancelRecurrence] =
    useState(false);

  const [disbursementParameters, setDisbursementParameters] = useState({});

  const { isLoading: isLoadingBankAccountsList, data: bankAccounts } =
    useGetAllChartOfAccounts({ sub_account_type: "cash_and_bank" });

  const [expenseDetails, setExpenseDetails] = useState({
    name: expense?.name,
    store: expense?.store?.id,
    store_label: expense?.store?.name,
    amount: expense?.amount,
    vat_rate: expense?.vat_rate,
    category: expense?.category?.id,
    category_label: expense?.category?.name,
    payment_method: expense?.payment_method,
    payment_method_label: expense?.payment_method,
    description: expense?.description,
    expense_date: expense?.expense_date,
    proof_url: expense?.proof_url,
    decline_reason: null,
    ledger_account_id: expense?.ledger_account_id,
    bank_code: expense?.bank_code || "",
    account_number: expense?.account_number || "",
    account_name: expense?.account_name || "",
    bank_code_label: "",
    department: expense?.department || "",
  });

  const [chargeVat, setChargeVat] = useState(expense?.vat_rate !== 0);

  const [expenseCategories, bankList] = useQueries([
    {
      queryKey: ["expenseCategories"],
      staleTime: Infinity,
      queryFn: () =>
        getExpenseCategories("").then((res) => {
          return res?.data?.data;
        }),
    },

    {
      queryKey: ["bankList"],
      staleTime: Infinity,
      enabled: true,
      queryFn: () =>
        fetchBankList("").then((res) => {
          return res?.data?.data;
        }),
    },
  ]);

  const { data, isLoading: isLoadingDepartments } = useGetExpenseDepartments(
    {},
  );

  const { mutate: makeRefundStatus, isLoading: isMakeRefundState } =
    useMakeRefundStatusMutation();

  const handleMakeRefundStatus = (status = false) => {
    const data = cleanObject({
      make_refund: true,
      bank_code: expenseDetails?.bank_code,
      account_number: expenseDetails?.account_number,
      account_name: expenseDetails?.account_name,
    });
    makeRefundStatus(
      { data, id: expense?.id },
      {
        onSuccess: (res) => {
          if (status) {
            triggerToast("Update Successful", "success");
            handleFetchExpenses();
            handleExpenseStatus("approve");
            setExpenseDetails((prevState) => ({
              ...prevState,
              make_refund: false,
            }));
          }
        },
      },
    );
  };

  const { mutate: expenseResponse, isLoading: isResponding } = useMutation(
    (data) => approveOrRejectExpenseWithoutDisbursement(data),
  );
  const handleExpenseStatus = (status) => {
    if (status === "decline" && !expenseDetails?.decline_reason)
      return triggerToast(
        "Please enter a reason for declining this expense",
        "warning",
      );
    if (
      (expense?.make_refund || expense?.payment_method === "PR") &&
      status === "approve"
    ) {
      setDisbursementParameters({
        transferTitle: "Review Disbursement",
        transferDesc: "Provide the following details for disbursement",
        amount: expenseDetails.amount,
        bank_code: expense?.bank_code || expenseDetails?.bank_code,
        bank_name:
          expense?.bank_name ||
          expense?.bank_code ||
          expenseDetails?.bank_code_label,
        account_number:
          expense?.account_number || expenseDetails?.account_number,
        account_name: expense?.account_name,
        useExternalDetails: true,
        isLoading: false,
        ledger_account_id: expenseDetails?.ledger_account_id,
        callBackFunc: handleDisbursement,
      });
      setIsDisbursmentForm(true);
      return;
    }

    setRequestLoaderProgress(getRandomIntInclusive(5, 40));
    delete expenseDetails.store_label;
    delete expenseDetails.make_refund;
    delete expenseDetails.category_label;
    delete expenseDetails.payment_method_label;
    setResponseStatus(status);
    expenseResponse(
      {
        data: expenseDetails,
        id: expense?.id,
        status: status,
      },
      {
        onSuccess: (res) => {
          triggerToast(res?.data?.detail, "success");
          handleFetchExpenses();
          closeWidget();
        },

        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  const handleExpenseStatusPr = (status) => {
    setRequestLoaderProgress(getRandomIntInclusive(5, 40));
    delete expenseDetails.store_label;
    delete expenseDetails.category_label;
    delete expenseDetails.payment_method_label;
    setResponseStatus(status);
    expenseResponse(
      {
        data: expenseDetails,
        id: expense?.id,
        status: status,
      },
      {
        onSuccess: (res) => {
          triggerToast(res?.data?.detail, "success");
          handleFetchExpenses();
          closeWidget();
        },
        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  const { mutate: disburseExpense } = useMutation((data) =>
    disburseExpenseFunds(data),
  );

  const handleDisbursement = (transferDetails) => {
    setDisbursementParameters((prev) => ({ ...prev, isLoading: true }));
    delete expenseDetails.store_label;
    delete expenseDetails.category_label;
    delete expenseDetails.payment_method_label;
    delete expenseDetails.decline_reason;
    delete expenseDetails.ledger_account_id;
    disburseExpense(
      {
        data: { ...expenseDetails, ...transferDetails },
        id: expense?.id,
      },
      {
        onSuccess: (res) => {
          triggerToast(res?.data?.detail, "success");
          handleFetchExpenses();
          closeWidget();
        },

        onSettled: () => {
          setRequestLoaderProgress(100);
          setDisbursementParameters((prev) => ({ ...prev, isLoading: false }));
        },
      },
    );
  };

  const { mutate: cancelRecurrence, isLoading: isCancellingRecurrence } =
    useCancelExpenseRecurrenceMutation({ id: expense?.id });

  const handleCancelRecurrence = () => {
    cancelRecurrence(expense?.id, {
      onSuccess: (res) => {
        triggerToast(
          res?.data?.detail || "Recurrence cancelled successfully",
          "success",
        );
        setIsConfirmingCancelRecurrence(false);
        handleFetchExpenses();
        closeWidget();
      },
    });
  };

  const { mutate: resolveAccount } = useMutation((data) =>
    resolveAccountNumber(data),
  );

  const handleResolveAccountNumber = (bankCode, accountNumber) => {
    resolveAccount(
      { bank_code: bankCode, account_number: accountNumber },
      {
        onSuccess: (res) => {
          setExpenseDetails((prevState) => ({
            ...prevState,
            account_name: res?.data?.data?.account_name,
          }));
        },
        onError: () => {
          setExpenseDetails((prevState) => ({
            ...prevState,
            account_name: null,
          }));
        },
      },
    );
  };

  // useEffect(() => {
  //   if (bankAccount?.data?.length > 0 && !expenseDetails?.ledger_account_id) {
  //     setExpenseDetails((prev) => ({
  //       ...prev,
  //       ledger_account_id: bankAccount?.data[0]?.id,
  //     }));
  //   }
  // });

  return {
    isLoadingForm:
      bankList.isLoading || expenseCategories.isLoading || isLoadingDepartments,
    expenseDetails,
    bankList: bankList.data || [],
    expenseCategories: expenseCategories?.data || [],
    expenseDepartments: data?.data || [],
    isDisbursmentForm,
    storeList,
    isResponding,
    responseStatus,
    disbursementParameters,
    setExpenseDetails,
    setIsDisbursmentForm,
    handleExpenseStatus,
    bankAccounts: bankAccounts?.data || [],
    makeRefundStatus,
    isMakeRefundState,
    handleMakeRefundStatus,
    isLoadingBankAccountsList,
    handleResolveAccountNumber,
    handleExpenseStatusPr,
    chargeVat,
    setChargeVat,
    handleCancelRecurrence,
    isCancellingRecurrence,
    isConfirmingCancelRecurrence,
    setIsConfirmingCancelRecurrence,
  };
};
export default useExpenseReview;
